import { useEffect, useRef, useState } from 'react'

import FacciaMascotte from './FacciaMascotte'
import { IS_TOUCH } from '../../config/touch'
import { giaGiocato } from '../../config/gumball'
import {
  cartStore, watchOpenStore, selectedProductStore,
  vicinoCassaStore, helpOpenStore, isUiOpen, gumballStore, statoGumballStore, segnalaCassa,
  useStore, cassaStore, avvisoGumballStore,
} from '../../store/uiStore'

// ============================================================
// LA GUIDA: la mascotte nell'angolo dello schermo.
//
// Era stata fatta prima in 3D, come una palla che girava per il negozio
// (recuperabile da git, commit "Mascotte 3D che gira per il negozio").
// Vive meglio qui: in un angolo è sempre a portata d'occhio, non ti si
// mette fra i piedi mentre cammini, e la frase è sempre leggibile invece
// di dipendere da quanto sei lontano.
//
// I CONSIGLI SONO CONTESTUALI e ordinati per PRIORITÀ: si dà il primo
// che ha senso adesso, non una frase a caso. Aggiungerne uno è una riga
// nell'array qui sotto.
//
// PARLA SOLO SE LA CLICCHI (chiesto dall'utente). Prima diceva la sua da
// sola, a intervalli che si diradavano; ora sta zitta e ALZA IL PUNTO
// ESCLAMATIVO quando ha qualcosa da dire — e il consiglio esce cliccandola.
// Chi non lo vuole non lo legge, e chi lo vuole sa dove trovarlo.
//
// NOTA: cliccarla vuol dire avere il CURSORE. Con la visuale agganciata
// (pointer lock) il mouse è catturato dal gioco e il cursore non c'è: lì la
// mascotte non si può cliccare, e sta zitta per definizione. Si clicca
// quando si esce dalla visuale — Esc, orologio, scheda prodotto aperta —
// che è poi quando si ha voglia di leggere.
// ============================================================

const DURATA = 7000    // per quanto resta scritto
const RICONTROLLA = 1500 // ogni quanto si guarda se c'è qualcosa da dire

// I TASTI SI SCRIVONO FRA PARENTESI QUADRE e vengono disegnati come
// tastini veri (vedi `conTasti`): "premi [Q]" si legge in un colpo d'occhio,
// mentre in mezzo alla frase una Q maiuscola è solo un'altra lettera.
// Su touch la tastiera NON C'È, quindi i consigli che parlano di tasti hanno
// una versione loro: dire "premi Q" a chi ha un telefono in mano è un
// consiglio sbagliato, non solo brutto.
const CONSIGLI = [
  {
    id: 'orologio',
    quando: (s) => !s.orologioVisto,
    testo: 'Premi [Q] e ti guardi l’orologio: lì dentro ci sono ricerca, comandi e carrello.',
    touch: 'Tocca [⌚] in alto a destra: lì dentro ci sono ricerca, comandi e carrello.',
  },
  {
    id: 'paga',
    quando: (s) => s.vicinoCassa && s.articoli > 0,
    testo: 'Ci siamo: premi [E] e il POS ti stampa lo scontrino.',
    touch: 'Ci siamo: tocca il pulsante in basso e il POS ti stampa lo scontrino.',
  },
  {
    id: 'cassa',
    quando: (s) => s.articoli > 0 && !s.vicinoCassa,
    testo: 'Hai roba nel carrello. Si paga al bancone: cerca il POS col riquadro celeste.',
  },
  {
    id: 'guarda',
    quando: (s) => !s.prodottoVisto,
    testo: 'Punta un prodotto sullo scaffale e premi [E] per guardarlo da vicino.',
    touch: 'Punta un prodotto sullo scaffale e tocca “Guarda” per vederlo da vicino.',
  },
  {
    id: 'gira',
    quando: (s) => s.prodottoVisto && s.articoli === 0,
    testo: 'Con un prodotto in mano, trascina il mouse per rigirartelo.',
    touch: 'Con un prodotto in mano, trascinaci sopra il dito per rigirartelo.',
  },
  {
    // Si accende da solo insieme alla macchinetta (statoGumballStore, cioe'
    // GUMBALL_ATTIVA=1 su Vercel): finche' e' spenta, parlarne sarebbe
    // mandare a cercare una cosa che non c'e'. E tace con chi ha gia'
    // giocato: ricordargliela quando non puo' rigiocare sarebbe una presa in giro.
    id: 'gumball',
    quando: (s) => statoGumballStore.get().funziona && !giaGiocato() && !s.gumballProvata,
    testo: 'Sul bancone c’è una macchinetta delle palline: mirala e premi [E] per girare la manopola.',
    touch: 'Sul bancone c’è una macchinetta delle palline: mirala e tocca “Gira la manopola”.',
  },
  {
    id: 'corri',
    quando: () => true,
    testo: 'Tieni premuto [Shift] per attraversare il negozio di corsa.',
    touch: 'Spingi la levetta fino in fondo per attraversare il negozio di corsa.',
  },
  {
    id: 'cerca',
    quando: (s) => s.orologioVisto,
    testo: 'Se cerchi qualcosa di preciso, c’è la lente fra le app dell’orologio.',
  },
]

// Spezza sulle parentesi quadre: con un gruppo di cattura, split() lascia i
// pezzi catturati nelle posizioni DISPARI, che sono quindi i tasti.
function conTasti(testo) {
  return testo.split(/\[([^\]]+)\]/).map((pezzo, i) => (
    i % 2 ? <kbd className="tasto" key={i}>{pezzo}</kbd> : pezzo
  ))
}

export default function Guida() {
  const [frase, setFrase] = useState(null)
  // Distingue "non ha ancora mai parlato" da "ha finito di parlare": senza,
  // al primo montaggio partirebbe l'animazione di USCITA del punto
  // esclamativo e si vedrebbe un lampo.
  const [parlato, setParlato] = useState(false)
  // C'è qualcosa da dire adesso? Decide il punto esclamativo, che è l'invito
  // a cliccarla: senza, sarebbe una palla muta che non si capisce cosa fa.
  const [haDaDire, setHaDaDire] = useState(false)
  // col tutorial aperto la mascotte dell'angolo sparisce: quella buona e'
  // l'altra (vedi il return piu' sotto)
  const tutorialAperto = useStore(helpOpenStore)

  // ------------------------------------------------------------
  // LE COSE CHE STANNO SUCCEDENDO LE DICE LEI.
  // Chiesto: i messaggi della stampa in corso e quelli della macchinetta
  // passano dalla mascotte invece di avere ciascuno il suo riquadro
  // fluttuante. Prima erano due pannelli diversi in due angoli diversi
  // (`.cassa-stato` in basso al centro, `.gumball-avviso` altrove), e il
  // negozio sembrava parlare con tre bocche. Ora c'e' una voce sola.
  //
  // NON E' UN CONSIGLIO: questi non si chiedono cliccando e non si
  // chiudono, perche' raccontano una cosa che sta succedendo adesso. Quindi
  // hanno la precedenza su quello che la mascotte direbbe di suo, e il
  // punto esclamativo (che e' l'invito a cliccarla) si spegne mentre
  // parlano.
  // La sorgente resta dov'e': niente store nuovo da tenere in sincrono, si
  // leggono i due che gia' esistono.
  // ------------------------------------------------------------
  const faseCassa = useStore(cassaStore)
  const avvisoGumball = useStore(avvisoGumballStore)
  const fraseCassa = faseCassa === 'attesa'
    ? 'Un attimo, sto elaborando…'
    : faseCassa === 'stampa' ? 'Ti sto stampando lo scontrino…' : null
  const fraseForzata = avvisoGumball || fraseCassa
  // il pallino che pulsa serve a dire "in corso", e lo merita solo la cassa:
  // gli avvisi della macchinetta sono fatti e finiti, non attese
  const inCorso = !avvisoGumball && !!fraseCassa
  // Cosa il giocatore ha già fatto. Sta in un ref e non nello stato: non
  // deve far ridisegnare niente, serve solo a scegliere la frase giusta
  // quando ne scatta una.
  const visto = useRef({ orologio: false, prodotto: false, gumball: false })
  const dette = useRef(new Set())

  useEffect(() => {
    const a = watchOpenStore.subscribe(() => {
      if (watchOpenStore.get()) visto.current.orologio = true
    })
    const b = selectedProductStore.subscribe(() => {
      if (selectedProductStore.get()) visto.current.prodotto = true
    })
    const c = gumballStore.subscribe(() => {
      if (gumballStore.get() !== 'ferma') visto.current.gumball = true
    })
    return () => { a(); b(); c() }
  }, [])

  // Il consiglio che ha senso ADESSO, o null. Si riparte da capo quando sono
  // stati detti tutti: il giro ricomincia, ma solo se lo si chiede.
  const scegli = () => {
    const cart = cartStore.get()
    const stato = {
      orologioVisto: visto.current.orologio,
      prodottoVisto: visto.current.prodotto,
      gumballProvata: visto.current.gumball,
      articoli: cart?.totalQuantity || 0,
      vicinoCassa: vicinoCassaStore.get(),
    }
    let scelto = CONSIGLI.find((c) => !dette.current.has(c.id) && c.quando(stato))
    if (!scelto) {
      dette.current.clear()
      scelto = CONSIGLI.find((c) => c.quando(stato))
    }
    return scelto || null
  }

  // Il punto esclamativo si accende e si spegne da solo; la FRASE no.
  // Con una finestra aperta si tace: finirebbe sotto al pannello. La tendina
  // dei comandi si controlla a parte perché NON sta in isUiOpen (quella
  // governa il mirino e il pointer lock, non la leggibilità).
  useEffect(() => {
    const guarda = () => {
      const libera = !isUiOpen() && !helpOpenStore.get()
      setHaDaDire(libera && !!scegli())
    }
    guarda()
    const t = setInterval(guarda, RICONTROLLA)
    return () => clearInterval(t)
  }, [])

  const spegni = useRef(null)
  const clic = () => {
    if (fraseForzata) return  // sta raccontando una cosa in corso: non si tocca
    if (frase) {            // ri-cliccandola la frase si chiude
      clearTimeout(spegni.current)
      setFrase(null)
      return
    }
    const scelto = scegli()
    if (!scelto) return
    dette.current.add(scelto.id)
    // Se il consiglio parla di pagare, il POS si illumina mentre lo dice:
    // "cerca il POS col riquadro celeste" senza il riquadro acceso e'
    // una caccia al tesoro.
    if (scelto.id === 'cassa' || scelto.id === 'paga') segnalaCassa()
    setFrase((IS_TOUCH && scelto.touch) || scelto.testo)
    setParlato(true)
    setHaDaDire(false)
    clearTimeout(spegni.current)
    spegni.current = setTimeout(() => setFrase(null), DURATA)
  }
  useEffect(() => () => clearTimeout(spegni.current), [])

  // LA MASCOTTE E' UNA SOLA. Col tutorial aperto ce n'e' gia' una nella sua
  // striscia, e questa in fondo allo schermo faceva due mascotte insieme —
  // segnalato come «si sdoppia». Quella dell'angolo si toglie di mezzo finche'
  // il tutorial parla, e torna esattamente quando la palla del tutorial vola
  // qui ad atterrare (il volo finisce chiudendo `helpOpenStore`).
  if (tutorialAperto) return null

  // quello che dice adesso: prima quello che sta succedendo, poi il consiglio
  const dice = fraseForzata || frase

  return (
    <div
      className={`guida${dice ? ' parla' : parlato ? ' tace' : ''}${haDaDire && !fraseForzata ? ' chiama' : ''}`}
      aria-live="polite"
    >
      {/* prima la palla, poi la nuvoletta: la guida sta nell'angolo in
          basso a sinistra, quindi la parola esce verso il centro dello
          schermo e l'angolo vivo del fumetto punta verso di lei */}
      {/* SOLO LA PALLA prende i click: il resto del contenitore deve restare
          trasparente ai click, perché sotto c'è il canvas e un click sul
          canvas è quello che riaggancia la visuale. */}
      <svg
        className="guida-palla"
        viewBox="0 0 220 220"
        role="button"
        tabIndex={0}
        aria-label={frase ? 'Chiudi il consiglio' : 'Chiedi un consiglio alla mascotte'}
        onClick={clic}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); clic() } }}
      >
        {/* Il sussulto sta su un GRUPPO INTERNO: il galleggiamento è sulla
            proprietà `translate` dell'svg, il sussulto su `transform` di
            questo gruppo, così le due animazioni non si contendono la
            stessa trasformazione e nessuna delle due va riscritta. */}
        <g className="guida-sussulto">
          <circle className="guida-corpo" cx="110" cy="110" r="78" />
          <FacciaMascotte />
        </g>
        {/* IL PUNTO ESCLAMATIVO: sbuca da dietro la testa quando c'è
            qualcosa da dire e resta esattamente quanto resta la frase.
            Sta DENTRO l'svg (che ha overflow visibile) così eredita il
            galleggiamento: due elementi separati andrebbero tenuti
            allineati a mano mentre la palla si muove. */}
        <g className="guida-avviso">
          {/* Il dondolio sta su un gruppo SUO: se stesse sullo stesso
              dell'entrata, l'uscita — che riparte da fermo — lo
              raddrizzerebbe di scatto proprio mentre se ne va. */}
          <g className="guida-avviso-dondolo">
            <circle className="guida-avviso-disco" cx="176" cy="-4" r="46" />
            <rect className="guida-avviso-segno" x="170.5" y="-30" width="11" height="33" rx="5.5" />
            <circle className="guida-avviso-segno" cx="176" cy="17" r="6" />
          </g>
        </g>
      </svg>
      <div className={dice ? 'guida-nuvola aperta' : 'guida-nuvola'}>
        {inCorso && <span className="guida-punto" />}
        {dice && conTasti(dice)}
      </div>
    </div>
  )
}
